import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import { useBreadcrumbContext } from '../context/BreadcrumbContext';

const DURATION = 350;
const TRANSITION = 'transition-[transform,opacity] ease-[cubic-bezier(0.32,0.72,0,1)]';

/** @type {import('react').FC<import('../lib/propTypes').BreadcrumbContentProps>} */

const BreadcrumbContent = ({ className, children }) => {
  const { current, step, direction } = useBreadcrumbContext();
  const content = current?.content ?? children;

  // One pane when idle. During a transition the previous step's pane keeps its
  // key (so its DOM survives and slides out without remounting) while the new
  // step's pane slides in beside it.
  const [panes, setPanes] = useState(() => [{ key: 0, node: content }]);
  // 'idle' | 'prep' (panes committed at start positions) | 'active' (sliding)
  const [phase, setPhase] = useState('idle');
  const containerRef = useRef(null);
  const prevStepRef = useRef(step);
  const latestContentRef = useRef(content);
  const phaseRef = useRef(phase);

  latestContentRef.current = content;
  phaseRef.current = phase;

  useLayoutEffect(() => {
    if (prevStepRef.current === step) {
      if (phaseRef.current === 'idle') {
        setPanes((prev) => {
          const last = prev[prev.length - 1];
          return last.node === content ? prev : [{ key: last.key, node: content }];
        });
      }
      return;
    }

    prevStepRef.current = step;
    setPanes((prev) => {
      const survivor = prev[prev.length - 1];
      return [survivor, { key: survivor.key + 1, node: latestContentRef.current }];
    });
    setPhase('prep');
  }, [step, content]);

  useLayoutEffect(() => {
    if (phase !== 'prep') return;
    // Force a style recalc at the start positions so both slides animate from
    // them instead of resolving straight to their final values
    containerRef.current?.getBoundingClientRect();
    setPhase('active');
  }, [phase]);

  useEffect(() => {
    if (phase !== 'active') return;
    const timeout = setTimeout(() => {
      setPanes((prev) => [{ key: prev[prev.length - 1].key, node: latestContentRef.current }]);
      setPhase('idle');
    }, DURATION);
    return () => clearTimeout(timeout);
  }, [phase]);

  const isMoving = phase !== 'idle' && panes.length === 2;
  const isForward = direction === 'forward';
  const leaveTarget = isForward ? '-translate-x-24 opacity-0' : 'translate-x-24 opacity-0';
  const enterFrom = isForward ? 'translate-x-24 opacity-0' : '-translate-x-24 opacity-0';

  const paneClass = (index) => {
    if (!isMoving) return '';
    const isLeaving = index === 0;
    if (phase === 'prep') {
      return isLeaving ? 'absolute inset-0 translate-x-0 opacity-100' : enterFrom;
    }
    return isLeaving
      ? cn('absolute inset-0', TRANSITION, leaveTarget)
      : cn(TRANSITION, 'translate-x-0 opacity-100');
  };

  return (
    <div ref={containerRef} className={cn('relative', isMoving && 'overflow-hidden', className)}>
      {panes.map((pane, index) => (
        <div
          key={pane.key}
          className={paneClass(index)}
          style={phase === 'active' ? { transitionDuration: `${DURATION}ms` } : undefined}>
          {pane.node}
        </div>
      ))}
    </div>
  );
};

export default BreadcrumbContent;
